// ─── FEES — single source of truth for Bloom's checkout money math (WEB) ──────
// This is a byte-for-byte mirror of the iOS app's Bloom/Models/Fees.swift. The two
// MUST agree exactly (rounding included) or web and app will charge different rupees
// for the same cart. Any change here has to be mirrored in Fees.swift, and vice-versa.
//
// Bloom collects ALL money centrally (one Razorpay account) and pays each store its
// `amount` manually. Two fees ride on the store totals, each governed by its OWN
// INDEPENDENT per-store toggle (2026-08-11): the gateway fee by bloom_users.gateway_fee_payer
// and the platform fee by bloom_users.platform_fee_payer. Either can be "customer" or "seller"
// independently (all four combos valid). Historically ONE toggle drove both; existing stores
// are backfilled platform_fee_payer = gateway_fee_payer so their behaviour is unchanged.
//   1) GATEWAY FEE (LIVE): Razorpay's cut — 2% transaction + 0.15% same-day-settlement,
//      +18% GST = 2.54% — encoded as the gross-up factor 0.9746. (Same-day fee folded in
//      here 2026-07-30 so the fee-bearer covers it; was 0.9764/2.36% before same-day.)
//        • "customer" (default) → buyer pays it on top: gross = ceil(total/0.9746).
//        • "seller"  (absorb)   → buyer pays flat total; store nets total×0.9746.
//   2) PLATFORM FEE: a tiered Bloom fee. Bloom KEEPS it. Governed by the SAME toggle:
//        • "customer" → buyer pays the fee ON TOP (grossed up); the store still gets its
//          full `amount`. Bloom nets the full fee.
//        • "seller"  (absorb) → buyer pays the flat total; the fee is taken OUT of the
//          store's `amount` (seller bears it, payout drops). Bloom keeps it.
//      Each fee has its own payer toggle; a single fee is never split across payers, but the
//      two fees can now go to different payers (e.g. store absorbs platform, customer pays gateway).
//
// MODEL: for an absorb store the platform fee is removed from `amount` (amount = total −
// fee) rather than added to the customer charge. That keeps the seller-payout formula
// (amount×0.9746) and the settlement reconciler untouched — the reduced amount naturally
// pays the seller less, and the reconciliation invariant below still holds exactly.
//
// Reconciliation invariant that MUST hold across an order's rows:
//     Σ amount + Σ tsf + Σ platform_fee == grossAmount   (what the customer pays)
// Every store row now carries its OWN platform_fee (perStorePlatformFee[idx]); the
// customer-fee gross-up lands on the first customer-paid store's tsf.

export const GST_FACTOR = 0.9746;

// ── Platform-fee kill-switch ──
// Flip in lockstep with the app's Fees.platformFeeEnabled. While false, platformFee is
// always 0, no platform_fee is stamped on orders, and checkout is byte-identical to the
// gateway-only behavior. This build ships it ON (the go-live); set false + redeploy to kill.
export const PLATFORM_FEE_ENABLED = true;

// Tiered platform-fee rate, chosen from the GRAND order total (sum of every store's
// post-discount total). Scales down as the order grows. 0 while the switch is off.
// Tiers finalized 2026-07-06 — MUST stay identical to Fees.swift's table.
export function platformPct(grandTotal) {
  if (!PLATFORM_FEE_ENABLED) return 0;
  if (grandTotal < 5000)  return 0.021;   // under ₹5,000     → 2.1%
  if (grandTotal < 9000)  return 0.019;   // ₹5,000–8,999     → 1.9%
  if (grandTotal < 12000) return 0.0125;  // ₹9,000–11,999    → 1.25%
  return 0.010;                           // ₹12,000+         → 1.0%
}

// Compute the full checkout money breakdown.
// inputs: [{ total, absorbs }], inputs[0] = PRIMARY store.
// Returns {
//   grossAmount,        // total the customer is charged
//   orderTotal,         // Σ store totals (pre-fee)
//   gatewayFee,         // displayed transaction/gateway line (customer-borne)
//   platformFee,        // Bloom's TOTAL earning across ALL stores (for records/earnings)
//   platformFeeCharged, // fee the BUYER pays on top (summary line) — excludes absorb fees
//   platformPct,
//   perStoreTsf,        // gateway tsf per store (+ customer-fee gross-up on first cust store)
//   perStoreAmount,     // amount to stamp per store (absorb = total − fee)
//   perStorePlatformFee // platform_fee to stamp per store (all stores)
// }.
// Each store now has TWO INDEPENDENT choices (2026-08-11). Inputs accept either the new
// per-fee flags OR the legacy single `absorbs` (which then applies to both — back-compat):
//   absorbsGateway  → store bears the Razorpay/transaction fee (no customer gross-up;
//                     seller nets amount×GST_FACTOR at payout).
//   absorbsPlatform → store bears Bloom's platform fee (taken OUT of `amount`).
// All four combos are valid. The two fees are handled fully independently below, and the
// per-store gw/pf-absorbed flags are RETURNED so checkout can stamp them on each order row
// (settlement reads those flags, NOT the `tsf>0` heuristic which breaks once fees split).

/**
 * Compute the full checkout money breakdown.
 * @param {Array<{total: number, absorbs?: boolean, absorbsGateway?: boolean, absorbsPlatform?: boolean, storeId?: string}>} inputs
 * @param {Object} opts — optional overrides for this calculation only:
 *   @param {number} [opts.gatewayFactor] — the F to use. Must be a number. Defaults to GST_FACTOR (0.9746).
 *   @param {number} [opts.platformPct]   — the platform % to use. Must be a number. Defaults to platformPct(orderTotal).
 * @returns {Object}
 */
export function quote(inputs, opts = {}) {
  const orderTotal = inputs.reduce((s, i) => s + i.total, 0);
  // opts overrides take precedence; fallback to defaults ensures backward compatibility.
  // F resolves to opts.gatewayFactor (if a number) or GST_FACTOR (default).
  // pct resolves to opts.platformPct (if a number and PLATFORM_FEE_ENABLED) or tiered platformPct(orderTotal).
  const F = typeof opts.gatewayFactor === 'number' ? opts.gatewayFactor : GST_FACTOR;
  const pct = typeof opts.platformPct === 'number' && PLATFORM_FEE_ENABLED ? opts.platformPct : platformPct(orderTotal);

  const flags = inputs.map(s => ({
    ag: ("absorbsGateway"  in s) ? !!s.absorbsGateway  : !!s.absorbs,
    ap: ("absorbsPlatform" in s) ? !!s.absorbsPlatform : !!s.absorbs,
  }));

  const perStoreTsf = [];
  const perStoreAmount = [];
  const perStorePlatformFee = [];
  const perStoreGwAbsorbed = [];
  const perStorePfAbsorbed = [];
  let grossStores = 0;

  inputs.forEach((s, idx) => {
    const { ag, ap } = flags[idx];
    const fee   = Math.round(s.total * pct);                              // every store owes the fee
    const gross = ag ? s.total : Math.ceil(s.total / F);                   // gateway gross-up ONLY if customer pays gateway
    perStoreTsf.push(Math.round(gross - s.total));
    // platform absorb → fee comes OUT of amount (seller bears it); else seller keeps full total.
    perStoreAmount.push(ap ? s.total - fee : s.total);
    perStorePlatformFee.push(fee);
    perStoreGwAbsorbed.push(ag);
    perStorePfAbsorbed.push(ap);
    grossStores += gross;
  });

  // Only stores where the CUSTOMER pays the platform fee (!ap) charge it on top (grossed up).
  const platformFeeCustomer = inputs.reduce(
    (s, i, idx) => s + (flags[idx].ap ? 0 : perStorePlatformFee[idx]), 0);
  const platformGrossUp = platformFeeCustomer > 0 ? Math.ceil(platformFeeCustomer / F) : 0;
  const platformTsf = platformGrossUp - platformFeeCustomer;

  // Fold the customer platform-fee gross-up into the FIRST customer-pays-platform store's tsf.
  const firstPfCust = flags.findIndex(f => !f.ap);
  if (firstPfCust >= 0) perStoreTsf[firstPfCust] += platformTsf;

  const grossAmount = Math.round(grossStores) + platformGrossUp;

  const platformFee = perStorePlatformFee.reduce((a, b) => a + b, 0); // Bloom's total earning
  const platformFeeCharged = platformFeeCustomer;                     // what the buyer sees
  // Displayed gateway/transaction fee — derived so the customer-facing lines
  // (orderTotal + gatewayFee + platformFeeCharged) always sum to grossAmount.
  const gatewayFee = grossAmount - Math.round(orderTotal) - platformFeeCustomer;

  return {
    grossAmount, orderTotal, gatewayFee,
    platformFee, platformFeeCharged, platformPct: pct,
    perStoreTsf, perStoreAmount, perStorePlatformFee,
    perStoreGwAbsorbed, perStorePfAbsorbed,
  };
}

// The seller's actual payout for one order row, from the payer flags stamped at order time.
// gw_absorbed → the store bore the gateway fee, so ×GST_FACTOR is applied; else full amount.
// (amount already reflects the platform choice.) Falls back to the legacy tsf>0 heuristic for
// OLD rows that predate the flags, so historical payouts are byte-identical.
// True when the store bore the GATEWAY fee for this order row → its payout gets ×GST_FACTOR.
// Reads the flag stamped at order time; falls back to the legacy tsf===0 rule for pre-split
// rows (byte-identical historical payouts). Settlement MUST use this, never a bare tsf>0 check
// (the platform-fee gross-up can push tsf>0 on a gateway-absorbing store once fees are split).
export function gwAbsorbed(o) {
  return (o.gw_absorbed != null) ? !!o.gw_absorbed : (Number(o.tsf || 0) === 0);
}
// Who bore the platform fee on this order. Rows from before the split have no
// pf_absorbed stamp; one toggle governed both fees then, so they follow the gateway.
export function pfAbsorbed(o) {
  return (o.pf_absorbed != null) ? !!o.pf_absorbed : gwAbsorbed(o);
}
// The gateway_factor that was ACTUALLY live for this order's charge, stamped by
// rzp-create-order at checkout time. A gateway-fee-absorbing store's payout below must use
// THIS rate, not the flat default — once MOP lets a customer choose Card (0.957) instead of
// UPI/netbanking (0.9746), the default would overpay the seller (and Bloom eats the gap) on
// every absorbed order where the customer picked a higher-fee method. Falls back to
// GST_FACTOR for legacy/pre-MOP rows (no stamp) and defensively for any invalid value.
function gwFactorUsed(o) {
  const f = Number(o.gw_factor_used);
  return (Number.isFinite(f) && f > 0) ? f : GST_FACTOR;
}
export function sellerPayout(o) {
  const amount = Number(o.amount || 0);
  return gwAbsorbed(o) ? Math.round(amount * gwFactorUsed(o)) : amount;
}

// Derive the two independent absorb flags from a store record (the seller's own settings OR a
// public _storeInfo payload). platform_fee_payer NULL → inherit gateway_fee_payer (covers rows
// before the backfill and any older store payload that omits it). Use everywhere a checkout
// builds `quote()` inputs so all surfaces (cart, buy-now, marketplace, product page) agree.
export function feeFlags(store) {
  return {
    absorbsGateway:  store?.gateway_fee_payer === "seller",
    absorbsPlatform: (store?.platform_fee_payer || store?.gateway_fee_payer) === "seller",
  };
}

// Human label for the platform-fee line, e.g. "2.1%", "1.25%", "1%".
export function platformPctLabel(pct) {
  const p = pct * 100;
  return `${Number.isInteger(p) ? p : parseFloat(p.toFixed(2))}%`;
}
